import React, {
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useRef
} from 'react'
import { useLoading } from './LoadingProvider'

export const SOUNDS = {
  CODIGO_10_10: 'sounds/codigo_10_10.mp3',
  CODIGO_20_20: 'sounds/codigo_20_20.mp3',
  CODIGO_DESCONOCIDO: 'sounds/QrRead/CODIGO_DESCONOCIDO.mp3',
  INVITADO_AUTORIZADO: 'sounds/QrRead/INVITADO_AUTORIZADO.mp3',
  INVITADO_NO_AUTORIZADO: 'sounds/QrRead/INVITADO_NO_AUTORIZADO.mp3',
  RESIDENTE_AUTORIZADO: 'sounds/QrRead/RESIDENTE_AUTORIZADO.mp3',
  RESIDENTE_NO_AUTORIZADO: 'sounds/QrRead/RESIDENTE_NO_AUTORIZADO.mp3',
  TRABAJADOR_AUTORIZADO: 'sounds/QrRead/TRABAJADOR_AUTORIZADO.mp3',
  TRABAJADOR_NO_AUTORIZADO: 'sounds/QrRead/TRABAJADOR_NO_AUTORIZADO.mp3'
} as const

export type SoundKey = keyof typeof SOUNDS

type AudioContextValue = {
  play: (key: SoundKey) => void
  stop: (key: SoundKey) => void
  isUnlocked: () => boolean
}

const AudioCtx = createContext<AudioContextValue | undefined>(undefined)

type Props = { children: ReactNode }

export const AudioProvider: FC<Props> = ({ children }) => {
  const { isElectron } = useLoading()
  const audiosRef = useRef<Partial<Record<SoundKey, HTMLAudioElement>>>({})
  const unlockedRef = useRef(false)
  const audioContextRef = useRef<AudioContext | null>(null)

  const ensureAudio = (key: SoundKey): HTMLAudioElement => {
    let el = audiosRef.current[key]
    if (!el) {
      el = new Audio(SOUNDS[key])
      el.preload = 'auto'
      audiosRef.current[key] = el
    }
    return el
  }

  // Prime all audio elements + AudioContext so a future .play() triggered by
  // a socket event (not by a user gesture) is allowed by the browser.
  const unlock = () => {
    if (unlockedRef.current) return
    unlockedRef.current = true

    ;(Object.keys(SOUNDS) as SoundKey[]).forEach(key => {
      const audio = ensureAudio(key)
      const prevMuted = audio.muted
      audio.muted = true
      const p = audio.play()
      if (p !== undefined) {
        p.then(() => {
          audio.pause()
          audio.currentTime = 0
          audio.muted = prevMuted
        }).catch(() => {
          audio.muted = prevMuted
        })
      }
    })

    try {
      const AC =
        (window as unknown as { AudioContext?: typeof AudioContext }).AudioContext ||
        (window as unknown as { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext
      if (AC && !audioContextRef.current) {
        audioContextRef.current = new AC()
      }
      if (audioContextRef.current?.state === 'suspended') {
        audioContextRef.current.resume().catch(() => {})
      }
    } catch {
      /* ignore */
    }
  }

  useEffect(() => {
    if (isElectron) {
      unlock()
      return
    }

    const events = [
      'click',
      'keydown',
      'touchstart',
      'pointerdown',
      'mousemove',
      'scroll'
    ] as const

    const handler = () => {
      unlock()
      events.forEach(e => document.removeEventListener(e, handler))
    }

    events.forEach(e => document.addEventListener(e, handler))
    return () => {
      events.forEach(e => document.removeEventListener(e, handler))
    }
  }, [isElectron])

  const play = (key: SoundKey) => {
    const audio = ensureAudio(key)

    if (audioContextRef.current?.state === 'suspended') {
      audioContextRef.current.resume().catch(() => {})
    }

    try {
      audio.currentTime = 0
    } catch {
      /* algunos navegadores lanzan si el audio aun no cargo */
    }

    const attempt = () => {
      const p = audio.play()
      if (p !== undefined) {
        p.catch(error => {
          console.error(`Error reproduciendo audio "${key}":`, error)
        })
      }
    }

    if (audio.readyState >= 2) {
      attempt()
    } else {
      const onReady = () => {
        audio.removeEventListener('canplay', onReady)
        attempt()
      }
      audio.addEventListener('canplay', onReady, { once: true })
      audio.load()
    }
  }

  const stop = (key: SoundKey) => {
    const audio = audiosRef.current[key]
    if (!audio) return
    audio.pause()
    try {
      audio.currentTime = 0
    } catch {
      /* ignore */
    }
  }

  const isUnlocked = () => unlockedRef.current

  return (
    <AudioCtx.Provider value={{ play, stop, isUnlocked }}>
      {children}
    </AudioCtx.Provider>
  )
}

export const useAudio = () => {
  const ctx = useContext(AudioCtx)
  if (!ctx) {
    throw new Error('useAudio must be used within AudioProvider')
  }
  return ctx
}
